import React, { useEffect, useRef, useState } from "react";
import { api, CURRENCIES, getCurrency } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Building2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function BusinessSettings() {
  const { reloadBusiness } = useAuth();
  const [biz, setBiz] = useState(null);
  const [form, setForm] = useState({
    name: "", address: "", city: "", country: "",
    phone: "", email: "", tax_id: "",
    currency_code: "PKR", currency_symbol: "₨",
    logo_data_uri: "",
  });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const fetchBiz = async () => {
    const r = await api.get("/business/me");
    setBiz(r.data);
    const code = r.data.currency_code || "PKR";
    const cur = getCurrency(code);
    setForm({
      name: r.data.name || "",
      address: r.data.address || "",
      city: r.data.city || "",
      country: r.data.country || "",
      phone: r.data.phone || "",
      email: r.data.email || "",
      tax_id: r.data.tax_id || "",
      currency_code: code,
      currency_symbol: r.data.currency_symbol || cur.symbol,
      logo_data_uri: r.data.logo_data_uri || "",
    });
  };

  useEffect(() => { fetchBiz(); }, []);

  const onCurrencyChange = (code) => {
    const cur = getCurrency(code);
    setForm((f) => ({ ...f, currency_code: code, currency_symbol: cur.symbol }));
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo too large. Please choose an image under 500 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_data_uri: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    setForm((f) => ({ ...f, logo_data_uri: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.name.trim()) {
      toast.error("Business Name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      await api.put("/business/me", payload);
      toast.success("Business details saved");
      await fetchBiz();
      await reloadBusiness();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!biz) return <div className="p-10 text-sm text-[#6B7280]" data-testid="biz-loading">Loading…</div>;

  return (
    <div className="p-10 max-w-4xl" data-testid="business-settings-page">
      <PageHeader overline="Workspace" title="Business Settings" testid="business-header" />

      {/* Subtle at-a-glance brand preview — small logo + name + city/country */}
      <div className="flex items-center gap-3 mb-6 pb-6 border-b border-[#E5E7EB]" data-testid="brand-preview">
        <div className="w-10 h-10 border border-[#E5E7EB] bg-white flex items-center justify-center overflow-hidden shrink-0">
          {form.logo_data_uri ? (
            <img src={form.logo_data_uri} alt="" className="w-full h-full object-contain" />
          ) : (
            <Building2 className="h-5 w-5 text-[#9CA3AF]" />
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#0F172A] truncate" data-testid="brand-preview-name">{form.name || <em className="text-[#9CA3AF] font-normal">Unnamed workspace</em>}</div>
          {(form.city || form.country) && (
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">
              {[form.city, form.country].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-8" data-testid="business-form">
        {/* Logo */}
        <section className="border border-[#E5E7EB] bg-white p-6">
          <div className="overline mb-4">Brand</div>
          <div className="flex items-start gap-6">
            <div className="w-32 h-32 border-2 border-dashed border-[#E5E7EB] flex items-center justify-center bg-[#F9FAFB] overflow-hidden" data-testid="logo-preview">
              {form.logo_data_uri ? (
                <img src={form.logo_data_uri} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Building2 className="h-12 w-12 text-[#9CA3AF]" />
              )}
            </div>
            <div className="flex-1">
              <Label className="text-sm font-medium text-[#111827] mb-1 block">Business Logo</Label>
              <p className="text-xs text-[#6B7280] mb-3">PNG/JPG up to 500 KB. Square images work best.</p>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onLogoChange}
                  data-testid="logo-input" className="hidden" id="logoFile" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                  data-testid="logo-pick-button"
                  className="rounded-none h-9 text-xs uppercase tracking-wider font-semibold">
                  <Upload className="h-3.5 w-3.5 mr-2" /> Upload Logo
                </Button>
                {form.logo_data_uri && (
                  <Button type="button" variant="outline" onClick={clearLogo}
                    data-testid="logo-clear-button"
                    className="rounded-none h-9 text-xs uppercase tracking-wider font-semibold text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/10 hover:text-[#EF4444]">
                    <X className="h-3.5 w-3.5 mr-2" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Details */}
        <section className="border border-[#E5E7EB] bg-white p-6 space-y-5">
          <div className="overline">Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="overline mb-1.5 block">Business Name <span className="text-[#EF4444]">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={1}
                data-testid="biz-name" className="rounded-none h-10" placeholder="Required — shown on PDF/CSV exports" />
              <p className="text-[10px] text-[#6B7280] mt-1">Required. New ledger entries, parties, or categories can only be added once this is set.</p>
            </div>
            <div>
              <Label className="overline mb-1.5 block">Tax ID / NTN</Label>
              <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                data-testid="biz-tax" className="rounded-none h-10 font-mono" placeholder="0123456-7" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="biz-phone" className="rounded-none h-10" placeholder="+92-300-1234567" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="biz-email" className="rounded-none h-10" placeholder="info@yourbusiness.com" />
            </div>
            <div className="md:col-span-2">
              <Label className="overline mb-1.5 block">Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="biz-address" rows={2} className="rounded-none"
                placeholder="Street / building details" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="biz-city" className="rounded-none h-10" placeholder="e.g. Karachi" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Country</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="biz-country" className="rounded-none h-10" placeholder="e.g. Pakistan" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Currency</Label>
              <Select value={form.currency_code} onValueChange={onCurrencyChange}>
                <SelectTrigger data-testid="biz-currency" className="rounded-none h-10">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`currency-opt-${c.code}`}>
                      <span className="font-mono mr-2">{c.symbol}</span>
                      <span className="font-medium">{c.code}</span>
                      <span className="text-[#6B7280] ml-2">— {c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#6B7280] mt-1">
                Selected symbol: <span className="font-mono font-semibold">{form.currency_symbol}</span> — shown in all amounts, totals, CSV and PDF exports.
              </p>
            </div>
            <div>
              <Label className="overline mb-1.5 block">Currency Symbol (override)</Label>
              <Input value={form.currency_symbol}
                onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })}
                data-testid="biz-currency-symbol"
                className="rounded-none h-10 font-mono"
                placeholder="e.g. ₨" maxLength={6} />
              <p className="text-[10px] text-[#6B7280] mt-1">Optional. Defaults to the symbol of the selected currency.</p>
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={saving} data-testid="biz-save-button"
            className="rounded-none h-10 px-6 bg-[#0F172A] hover:bg-[#1E293B] text-xs uppercase tracking-wider font-semibold">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
